import {
  AppInfoLink,
  Button,
  IconButton,
  Modal,
  PhoneInput,
  Select,
  Text,
  TextField,
  TutorialModal,
  LineAddFriendModalContent,
  LineLoginModalContent,
  Verify,
} from 'components';
import {
  COOKIE_KEYS,
  SESSION_KEYS,
  TEXT_STRING,
  routes,
} from '../../constants';
import {
  checkIdentify,
  login as loginApi,
  signup as signupApi,
  updateEmail,
} from 'api';
import { getMessagingToken } from 'configs';
import { useABTesting, useAuth, useChat, useLine, useToast } from 'hooks';
import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  TPhoneInput,
  TSelectOption,
  TTextFieldChangeEvent,
  TUpdateEmail,
} from 'types';
import {
  convertFormatAmount,
  getCookies,
  getFormattedAmount,
  isInvalidLineSessionError,
  isLineLoginEnabled,
  pushDataLayer,
  setCookie,
  validateEmail,
  validatePhone,
} from 'utils';
import { Country } from 'react-phone-number-input';
import { useAppStore, useOrderStore, useUserStore } from 'store';
import Banner from '../../assets/images/registerBanner.png';

const loginStr = TEXT_STRING.LOGIN;
const commonStr = TEXT_STRING.COMMON;
const introducingStr = TEXT_STRING.INTRODUCING;

const validateString = (value: string) => !value || !!value.trim();

enum EFieldRegister {
  AREA = 'areaId',
  NAME = 'nickname',
  PHONE = 'phone',
  EMAIL = 'email',
  AGE = 'age',
  INCOME = 'income',
}

const VERIFY_FLOW = ['LINE', 'LINE_FRIEND', 'PHONE', 'OTP'] as const;
type TVerifyStep = (typeof VERIFY_FLOW)[number];

const fields = [
  {
    name: EFieldRegister.AREA,
    label: TEXT_STRING.CAST_PAGE.ACTIVITY_AREA,
    placeholder: TEXT_STRING.COMMON.SELECT,
    validate: validateString,
  },
  {
    name: EFieldRegister.NAME,
    label: loginStr.NICK_NAME,
    type: 'text',
    placeholder: loginStr.PLACEHOLDER_NICK_NAME,
    validate: validateString,
  },
  {
    name: EFieldRegister.PHONE,
    label: loginStr.PHONE_NUMBER,
    type: 'number',
    placeholder: loginStr.PLACEHOLDER_PHONE,
  },
  {
    name: EFieldRegister.EMAIL,
    label: loginStr.EMAIL,
    type: 'text',
    placeholder: loginStr.PLACEHOLDER_EMAIL,
    validate: validateEmail,
  },
];

type TFormState = {
  [x: string]: string;
};

const countryCode = (process.env.REACT_APP_COUNTRY_CODE || 'JP') as Country;

const affiliate_pid = process.env.REACT_APP_AFFILIATE_PID || '';

function Signup() {
  // State
  const [verifyStep, setVerifyStep] = useState<TVerifyStep>(
    isLineLoginEnabled ? 'LINE' : 'PHONE',
  );
  const [formState, setFormState] = React.useState<TFormState>({
    nickname: '',
    email: '',
    areaId: '',
    age: '',
    income: '',
  });
  const [phoneState, setPhoneState] = React.useState<TPhoneInput>({
    countryCode,
    number: '',
  });
  const [loading, setLoading] = React.useState<boolean>(false);
  const [invitationId, setInvitationId] = React.useState<string | null>();
  const ownsPostAuthNavigationRef = React.useRef(false);
  const isExistingAccountRef = React.useRef(false);

  // Hooks
  const { sendOTP, verifyOTP } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toastError } = useToast();
  const [searchParams] = useSearchParams();
  const { isUserApplyABTesting } = useABTesting();
  const {
    activeLineSessionToken,
    loginOrRedirectLineOAuth,
    mutateGetFriendStatus,
    clearLineSession,
    redirectLogin,
  } = useLine();
  const from = React.useMemo(
    () => location.state?.from?.pathname || '/',
    [location],
  );

  // Store
  const { isAuthenticated, clientId, subscriptionId, setIsAuthenticated } =
    useUserStore();
  const { redirectOrderStep } = useOrderStore();
  const { activityAreas } = useAppStore();
  const { handleChatWithAdmin } = useChat();

  const options = React.useMemo<TSelectOption[]>(
    () =>
      activityAreas
        ?.filter((item) => !item.isOtherArea)
        ?.map((item) => ({
          name: item.name,
          value: `${item.id}`,
        })) || [],
    [activityAreas],
  );

  const onChangeField = React.useCallback(
    (name: string, value: string) => {
      setFormState((prev: TFormState) => ({ ...prev, [name]: value }));
    },
    [setFormState],
  );

  const onChangeNumberField = React.useCallback((e: TTextFieldChangeEvent) => {
    const { name, value } = e.target;
    if (+value || +value === 0) {
      setFormState((prev) => ({ ...prev, [name]: value }));
    }
  }, []);

  const handleFocus = React.useCallback((e: TTextFieldChangeEvent) => {
    const { name, value } = e.target;
    if (value)
      setFormState((prev) => ({
        ...prev,
        [name]: `${convertFormatAmount(value)}`,
      }));
  }, []);

  const handleBlur = React.useCallback((e: TTextFieldChangeEvent) => {
    const { name, value } = e.target;
    if (value) {
      setFormState((prev) => ({ ...prev, [name]: getFormattedAmount(+value) }));
    }
  }, []);

  const onBack = React.useCallback(() => {
    if (!!location.state?.from) {
      navigate(-1);
    } else navigate(routes.SEARCH);
  }, [navigate, location.state]);

  const onCancel = React.useCallback(() => {
    onBack();
  }, [onBack]);

  const onSignup = React.useCallback(async () => {
    if (!validatePhone(phoneState) || !phoneState.number) {
      return;
    }

    try {
      setLoading(true);
      const identity = await checkIdentify(phoneState.number);
      if (identity.isDisabledUser) {
        throw new Error(identity.message);
      }
      const invalidFilled = fields.some(
        ({ name, validate }) =>
          name !== 'phone' &&
          !!formState[name] &&
          validate &&
          !validate(formState[name]),
      );
      const invalidNewUser = fields.some(
        ({ name, validate }) =>
          name !== 'phone' &&
          (!formState[name] || (validate && !validate(formState[name]))),
      );
      if (identity.isValidUser ? invalidFilled : invalidNewUser) {
        return;
      }
      isExistingAccountRef.current = identity.isValidUser;
      if (
        !identity.isValidUser &&
        isLineLoginEnabled &&
        activeLineSessionToken
      ) {
        const latestIsFriend = await mutateGetFriendStatus();
        if (!latestIsFriend) {
          setVerifyStep('LINE_FRIEND');
          return;
        }
      }
      await sendOTP(phoneState.number);
      setVerifyStep('OTP');
    } catch (error) {
      toastError(error as Error, { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  }, [
    phoneState,
    formState,
    sendOTP,
    toastError,
    activeLineSessionToken,
    mutateGetFriendStatus,
  ]);

  const onVerify = React.useCallback(
    async (otp: string) => {
      if (!phoneState.number || !validatePhone(phoneState)) {
        toastError(new Error('電話番号が正しくありません。'), {
          position: 'top-center',
        });
        return;
      }

      ownsPostAuthNavigationRef.current = true;
      let registeredId: number | undefined;
      try {
        setLoading(true);
        const token = await verifyOTP(otp);
        if (!token) {
          return;
        }

        if (isExistingAccountRef.current) {
          const fcmToken = await getMessagingToken();
          await loginApi(
            token,
            fcmToken,
            subscriptionId,
            activeLineSessionToken,
          );
          setIsAuthenticated(true);
        } else {
          const affiliate_cid = localStorage.getItem(`CL_${affiliate_pid}`);
          const { id } = await signupApi(
            {
              name: formState.nickname,
              email: formState.email,
              areaId: +formState.areaId,
              phone: phoneState.number || '',
              clientId: clientId || '',
              age: convertFormatAmount(formState.age),
              income: convertFormatAmount(formState.income),
              inviteId: invitationId,
              affiliateInfo: {
                affiliate_pid: affiliate_pid,
                affiliate_cid: affiliate_cid,
                affiliate_mid: localStorage.getItem('mid'),
                affiliater_id: localStorage.getItem('affliater_id'),
              },
              lineSessionToken: activeLineSessionToken,
            },
            token,
          );
          setIsAuthenticated(true);
          registeredId = id;
        }
      } catch (error) {
        if (isInvalidLineSessionError(error)) {
          clearLineSession();
          await redirectLogin();
          return;
        }
        toastError(error as Error, { position: 'top-center' });
        return;
      } finally {
        setLoading(false);
      }

      if (registeredId != null) {
        try {
          const TRACKING_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
          if (TRACKING_ID) {
            if (!ReactGA.isInitialized) {
              ReactGA.initialize(TRACKING_ID);
            }
            ReactGA.event('sign_up', { teco_id: registeredId });
          }
          pushDataLayer({ event: 'sign_up', label: `${registeredId}` });
        } catch {
          // 計測の失敗は遷移を止めない
        }
      }

      try {
        if (isExistingAccountRef.current) {
          const profile: TUpdateEmail = {};
          if (formState.nickname && validateString(formState.nickname)) {
            profile.nickname = formState.nickname.trim();
          }
          if (formState.email && validateEmail(formState.email)) {
            profile.email = formState.email;
          }
          if (formState.areaId) profile.areaId = +formState.areaId;
          if (Object.keys(profile).length > 0) {
            await updateEmail(profile);
          }
        }
      } catch {
        // プロフィール更新の失敗はセッションを取り消さない
      }

      navigate(routes.MY_PAGE, { replace: true });
      redirectOrderStep();
    },
    [
      verifyOTP,
      formState,
      phoneState,
      redirectOrderStep,
      toastError,
      invitationId,
      navigate,
      clientId,
      subscriptionId,
      setIsAuthenticated,
      activeLineSessionToken,
      clearLineSession,
      redirectLogin,
    ],
  );

  const onGoToLogin = React.useCallback(() => {
    // 保護ルートから送られてきた場合、ログイン後に戻す先を落とさない
    navigate(routes.LOGIN, {
      replace: true,
      state: location.state,
    });
  }, [navigate, location.state]);

  // Effect
  useEffect(() => {
    const paramId =
      searchParams.get('invite') ||
      sessionStorage.getItem(SESSION_KEYS.INVITE_ID) ||
      getCookies(COOKIE_KEYS.INVITE_ID);

    if (paramId) {
      setCookie([{ key: COOKIE_KEYS.INVITE_ID, value: paramId }]);
      sessionStorage.setItem(SESSION_KEYS.INVITE_ID, paramId);
    }
    setInvitationId(paramId);
    return () => {};
  }, [searchParams]);

  // Effect
  useEffect(() => {
    const syncVerifyStep = async () => {
      if (!isLineLoginEnabled) {
        setVerifyStep('PHONE');
        return;
      }

      if (!activeLineSessionToken) {
        setVerifyStep('LINE');
        return;
      }

      setVerifyStep('PHONE');
    };

    void syncVerifyStep();
  }, [activeLineSessionToken]);

  if (isAuthenticated && !ownsPostAuthNavigationRef.current) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    // return <h1>Protected Route</h1>;
    return <Navigate to={from} state={{ from: location }} replace />;
  }

  return (
    <div className="absolute top-0 bg-black w-full h-full z-10">
      {isUserApplyABTesting ? (
        <TutorialModal
          open={true}
          trigger="signup"
          onClose={onBack}
          text={{
            title: TEXT_STRING.INTRODUCING.REGISTER_NOTE,
          }}
        />
      ) : (
        <Modal
          open={true}
          onClose={() => {}}
          className={`px-4 py-6 mx-5 bg-white w-full`}
          positionLayout="fixed"
        >
          {verifyStep === 'LINE' && (
            <LineLoginModalContent
              onCancel={onCancel}
              onClick={loginOrRedirectLineOAuth}
              loading={loading}
              disabled={loading}
            />
          )}
          {verifyStep === 'LINE_FRIEND' && (
            <LineAddFriendModalContent
              onSuccess={() => setVerifyStep('PHONE')}
              onClose={() => setVerifyStep('PHONE')}
            />
          )}
          {verifyStep === 'PHONE' && (
            <>
              <IconButton
                icon="close"
                transparent
                className="fixed top-0 right-0 z-50 m-4"
                onClick={onCancel}
              />
              <div className="overflow-y-auto overflow-x-hidden max-h-[70vh] px-1">
                <div className="bg-white flex flex-col gap-5">
                  {invitationId ? (
                    <div className={`mt-8`}>
                      <img src={Banner} alt="introducing-friend" />
                      <Text textColor="text-neutral" center fontSize={13}>
                        {introducingStr.TECO_ASSET_NOTE}
                      </Text>
                    </div>
                  ) : (
                    <Text center bold className="mt-8 mb-2">
                      {loginStr.MESSAGE_REGISTER}
                    </Text>
                  )}
                  {fields.map((field, index) =>
                    field.name === EFieldRegister.PHONE ? (
                      <PhoneInput
                        key={index}
                        name={field.name}
                        onChange={setPhoneState}
                        value={phoneState}
                        label={field.label}
                        type={field.type}
                        placeholder={field.placeholder}
                        error={!validatePhone(phoneState)}
                        disabled={loading}
                        require
                        inputClass="py-2.5"
                      />
                    ) : field.name === EFieldRegister.AREA ? (
                      <Select
                        key={index}
                        label={field.label}
                        name={field.name}
                        value={formState[field.name]}
                        onChange={(value) => onChangeField(field.name, value)}
                        options={options}
                        require
                        className="py-2.5"
                        bold
                      />
                    ) : [EFieldRegister.AGE, EFieldRegister.INCOME].includes(
                        field.name,
                      ) ? (
                      <TextField
                        key={index}
                        name={field.name}
                        onChange={onChangeNumberField}
                        value={formState[field.name]}
                        label={field.label}
                        type={field.type}
                        placeholder={field.placeholder}
                        error={
                          field.validate &&
                          !field.validate(formState[field.name])
                        }
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        require
                        bold
                      />
                    ) : (
                      <TextField
                        key={index}
                        name={field.name}
                        onChange={(e) =>
                          onChangeField(field.name, e.target.value)
                        }
                        value={formState[field.name]}
                        label={field.label}
                        type={field.type}
                        placeholder={field.placeholder}
                        error={
                          field.validate &&
                          !field.validate(formState[field.name])
                        }
                        require
                        bold
                      />
                    ),
                  )}
                  <div className="flex flex-col gap-2.5">
                    <Text bold fontSize={14}>
                      {loginStr.GO_LOGIN}{' '}
                      <span
                        className="text-primary cursor-pointer"
                        onClick={onGoToLogin}
                      >
                        {loginStr.HERE}
                      </span>
                    </Text>
                    <Text bold fontSize={14}>
                      {commonStr.LOGIN_HINT}{' '}
                      <span
                        className="text-primary cursor-pointer"
                        onClick={handleChatWithAdmin}
                      >
                        {loginStr.HERE}
                      </span>
                    </Text>
                    <AppInfoLink />
                  </div>
                </div>
              </div>
              <div className="mt-16px">
                <Button
                  onClick={onSignup}
                  block
                  className=""
                  disabled={loading}
                  loading={loading}
                >
                  {loginStr.REGISTRATION}
                </Button>
                <Button
                  onClick={onCancel}
                  block
                  textColor="text-primary"
                  bgColor="bg-white"
                  className="py-0 mt-4"
                  disabled={loading}
                >
                  {commonStr.CANCEL}
                </Button>
              </div>
            </>
          )}
          {verifyStep === 'OTP' && (
            <>
              {isExistingAccountRef.current && (
                <Text center>{loginStr.EXISTING_ACCOUNT_FOUND}</Text>
              )}
              <Verify
                phone={phoneState.number || ''}
                verifyOTP={onVerify}
                resendOTP={() => sendOTP(phoneState.number || '')}
                loading={loading}
              />
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

export default Signup;
